import { type ReactNode } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';

import { EntitySectionLayout } from '@/components/composed/EntitySectionLayout';
import { DateCell } from '@/components/composed/DateCell';
import { IdCell } from '@/components/composed/IdCell';
import { MaskedCell } from '@/components/composed/MaskedCell';
import { StatusBadge } from '@/components/composed/StatusBadge';
import { EmptyState } from '@/components/composed/EmptyState';
import { resolveSection } from '@/lib/entity-sections';
import { agentBrokerDetailQuery } from '../api/agent-broker-queries';
import type { AgentBrokerId } from '@/lib/api/ids';
import { agentBrokerSections } from '../agent-broker-sections';

/*
 * AgentBrokerDetail — clone of ProviderDetail, the first consumers of the
 * composed EntitySectionLayout.
 *
 * The route resolves the active section from the `$section` param and PUBLISHES
 * the entity sub-nav into the shell rail (via `useRailSubNav`). This
 * component owns only the domain wiring left in the content column: resolving
 * which body to render for the active section. It is navigation-mechanism-
 * agnostic — it never imports a router.
 */
export function AgentBrokerDetail({
  agentBrokerId,
  section,
}: {
  agentBrokerId: AgentBrokerId;
  /** Raw `$section` route param (may be missing/invalid). */
  section: string | undefined;
}) {
  const { data: agentBroker } = useSuspenseQuery(
    agentBrokerDetailQuery(agentBrokerId),
  );
  const activeSectionId =
    resolveSection(section, agentBrokerSections) ?? agentBrokerSections[0]!.id;

  return (
    <EntitySectionLayout>
      <SectionBody sectionId={activeSectionId} agentBroker={agentBroker} />
    </EntitySectionLayout>
  );
}

function SectionBody({
  sectionId,
  agentBroker,
}: {
  sectionId: string;
  agentBroker: import('@/lib/api/domains/agents-brokers').AgentBroker;
}) {
  if (sectionId === 'profile') {
    return (
      <dl className="grid grid-cols-2 gap-4 text-sm">
        <Field label="Name">
          <span className="font-medium">{agentBroker.name}</span>
        </Field>
        {agentBroker.dbaName ? (
          <Field label="DBA name">
            <span className="font-medium">{agentBroker.dbaName}</span>
          </Field>
        ) : null}
        {agentBroker.publicId ? (
          <Field label="Public ID">
            <IdCell value={agentBroker.publicId} />
          </Field>
        ) : null}
        {agentBroker.agentType ? (
          <Field label="Type">
            <span className="font-medium">{agentBroker.agentType}</span>
          </Field>
        ) : null}
        {agentBroker.npn ? (
          <Field label="NPN">
            <span className="font-medium">{agentBroker.npn}</span>
          </Field>
        ) : null}
        {agentBroker.taxId ? (
          <Field label="TIN">
            <MaskedCell value={agentBroker.taxId} />
          </Field>
        ) : null}
        <Field label="Status">
          <StatusBadge
            label={agentBroker.status === 'active' ? 'Active' : 'Inactive'}
            tone={agentBroker.status === 'active' ? 'success' : 'neutral'}
          />
        </Field>
        <Field label="Created">
          <DateCell value={agentBroker.createdAt} />
        </Field>
      </dl>
    );
  }

  // The remaining sections (licenses, commissions, contracts, bank-accounts)
  // have no FE contract yet — their endpoints are the entity sub-domains that
  // land in later slices. The layout + deep-link routing is fully wired now;
  // each section fills in when its data contract exists.
  const label =
    agentBrokerSectionLabel(sectionId) ?? sectionId.replace(/-/g, ' ');
  return (
    <EmptyState
      title={label}
      description="This section will be available in a later release."
    />
  );
}

function agentBrokerSectionLabel(id: string): string | undefined {
  return agentBrokerSections.find((s) => s.id === id)?.label;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-medium">{children}</dd>
    </div>
  );
}
